import { useEffect, useState } from 'react';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white/98 backdrop-blur-md shadow-sm' : 'bg-white/80 backdrop-blur-sm'
      }`}>
        <div className="max-w-[1400px] mx-auto px-20 h-24 flex items-center justify-between">
          <div className="flex items-center">
            <div className="flex flex-col">
              <span className="text-[#B8956A] text-3xl font-bold tracking-tight font-serif italic">ÍA</span>
              <span className="text-[#8B7355] text-[10px] font-light tracking-[0.2em] uppercase -mt-1">Desenvolvimento Comercial</span>
            </div>
          </div>
          
          <div className="flex items-center gap-10">
            <button 
              onClick={() => scrollToSection('metodologia')}
              className="text-[#6B5D52] hover:text-[#B8956A] transition-colors text-sm font-light cursor-pointer whitespace-nowrap"
            >
              Metodologia
            </button>
            <span className="text-[#E8DCC8]">•</span>
            <button 
              onClick={() => scrollToSection('pilares')}
              className="text-[#6B5D52] hover:text-[#B8956A] transition-colors text-sm font-light cursor-pointer whitespace-nowrap"
            >
              Pilares
            </button>
            <span className="text-[#E8DCC8]">•</span>
            <button 
              onClick={() => scrollToSection('qualificacao')}
              className="text-[#6B5D52] hover:text-[#B8956A] transition-colors text-sm font-light cursor-pointer whitespace-nowrap"
            >
              Qualificação
            </button>
            <span className="text-[#E8DCC8]">•</span>
            <button 
              onClick={() => scrollToSection('contato')}
              className="text-[#6B5D52] hover:text-[#B8956A] transition-colors text-sm font-light cursor-pointer whitespace-nowrap"
            >
              Contato
            </button>
          </div>

          <a 
            href="https://wa.me/5541998065890"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-[#B8956A] text-white px-8 py-3.5 rounded-full text-sm font-light hover:bg-[#A17E54] transition-all duration-300 flex items-center gap-2 cursor-pointer whitespace-nowrap shadow-lg"
          >
            Diagnóstico Estratégico
            <i className="ri-arrow-right-line"></i>
          </a>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-24 bg-gradient-to-br from-[#FAF8F5] via-white to-[#F5F1EB]">
        <div className="max-w-[1400px] mx-auto px-20 w-full">
          <div className="flex items-center gap-16">
            {/* Left Content - 60% */}
            <div className="w-[60%] py-20">
              <div className="inline-block border border-[#B8956A]/30 px-5 py-2.5 rounded-full mb-10">
                <span className="text-[#B8956A] text-[11px] font-medium tracking-[0.15em] uppercase">
                  ÍA Desenvolvimento Comercial • Gestão de Alta Performance
                </span>
              </div>

              <h1 className="text-[58px] leading-[1.15] font-light text-[#4A3F35] mb-10 font-serif">
                <span className="font-semibold italic">Estruturação Comercial Sistêmica</span>: Transformando a Complexidade Operacional em <span className="font-semibold text-[#B8956A]">Performance Previsível</span> e Escalável.
              </h1>

              <p className="text-[17px] leading-[1.7] text-[#6B5D52] mb-12 max-w-[90%] font-light">
                Para empresas que buscam superar o teto do faturamento atual através da profissionalização dos processos e alinhamento de talentos. Uma abordagem consultiva focada na arquitetura de negócios.
              </p>

              <a 
                href="https://wa.me/5541998065890"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 bg-[#B8956A] text-white px-12 py-5 rounded-full text-base font-light hover:bg-[#A17E54] hover:shadow-2xl transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                Solicitar Diagnóstico Estratégico
                <i className="ri-arrow-right-line"></i>
              </a>
            </div>

            {/* Right Image - 40% */}
            <div className="w-[40%] h-[calc(100vh-96px)] relative">
              <div className="absolute inset-0 rounded-l-[40px] overflow-hidden shadow-2xl">
                <img 
                  src="https://public.readdy.ai/ai/img_res/bf829c84478ed097d80f232bfc49a486.jpg"
                  alt="Executive Boardroom ÍA Desenvolvimento Comercial"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-br from-[#B8956A]/10 via-transparent to-white/20"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contexto e Maturidade */}
      <section className="bg-gradient-to-b from-white to-[#FAF8F5] py-32">
        <div className="max-w-[1100px] mx-auto px-20 text-center">
          <div className="inline-block mb-8">
            <span className="text-[#B8956A] text-xs font-medium tracking-[0.2em] uppercase">
              ÍA Desenvolvimento Comercial • O Problema
            </span>
          </div>

          <h2 className="text-[46px] font-light text-[#4A3F35] mb-12 font-serif max-w-[70%] mx-auto italic">
            O Desafio da Maturidade Comercial
          </h2>

          <div className="grid grid-cols-2 gap-16 text-left">
            <div className="text-[16px] leading-[1.9] text-[#6B5D52] font-light">
              <p>
                A maioria das empresas não sofre por falta de produtos, mas por <strong className="font-medium text-[#4A3F35]">fragilidade na arquitetura de vendas</strong>. Quando a venda depende do esforço heroico de indivíduos e não de um processo, o crescimento é instável.
              </p>
            </div>
            <div className="text-[16px] leading-[1.9] text-[#6B5D52] font-light">
              <p>
                O <strong className="font-medium text-[#4A3F35]">desalinhamento entre Marketing, Vendas e Operação</strong> é o maior dreno de lucratividade hoje. A transformação começa pela estruturação sistêmica.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Sobre a Estrategista */}
      <section className="py-32 bg-white">
        <div className="max-w-[1400px] mx-auto px-20">
          <div className="flex items-center gap-20">
            {/* Left Images - 45% */}
            <div className="w-[45%] relative h-[600px]">
              <div className="absolute top-0 left-0 w-[400px] h-[400px] rounded-[30px] overflow-hidden shadow-2xl border-4 border-[#B8956A]/20 z-10">
                <img 
                  src="https://static.readdy.ai/image/b1a50788a9da1edd0d71f16ab7984f8b/d3671c79e8eb7738057c5a960b3c2905.jpeg"
                  alt="ÍA Desenvolvimento Comercial Estrategista"
                  className="w-full h-full object-cover object-center"
                />
              </div>
              <div className="absolute bottom-0 right-0 w-[350px] h-[500px] rounded-[30px] overflow-hidden shadow-2xl bg-gradient-to-br from-[#FAF8F5] to-[#E8DCC8]">
                <img 
                  src="https://readdy.ai/api/search-image?query=Sophisticated%20business%20planning%20workspace%20with%20elegant%20cream%20and%20gold%20accents%2C%20luxury%20consulting%20materials%2C%20premium%20office%20interior%2C%20refined%20strategic%20documents%2C%20high-end%20corporate%20workspace%2C%20sophisticated%20desk%20setup%2C%20elegant%20business%20environment%2C%20warm%20beige%20tones&width=350&height=500&seq=workspace-elegant-002&orientation=portrait"
                  alt="Workspace ÍA Desenvolvimento Comercial"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            {/* Right Content - 55% */}
            <div className="w-[55%]">
              <div className="mb-6">
                <span className="text-[#B8956A] text-[11px] font-medium tracking-[0.2em] uppercase">
                  ÍA Desenvolvimento Comercial • Visão e Método
                </span>
              </div>

              <h2 className="text-[42px] font-light text-[#4A3F35] mb-10 font-serif italic">
                Visão, Bagagem e Método
              </h2>

              <div className="space-y-6 text-[16px] leading-[2.0] text-[#6B5D52] font-light">
                <p>
                  Atuação como <strong className="font-medium text-[#4A3F35]">arquiteta de estruturas comerciais</strong>. Especialista em Gestão Estratégica, Análise Comportamental (Pessoas e Times) e Inteligência de Dados.
                </p>
                <p>
                  Foco em organizar o caos, trazendo clareza para o líder e segurança para a execução. Não é treinamento motivacional; é <strong className="font-medium text-[#4A3F35]">engenharia de negócios</strong>.
                </p>
                <p>
                  A metodologia da <strong className="font-medium text-[#B8956A]">ÍA Desenvolvimento Comercial</strong> combina diagnóstico profundo, desenho customizado e implementação orientada por dados, garantindo que cada decisão esteja alinhada com os objetivos estratégicos da organização.
                </p>
              </div>

              <a 
                href="#pilares"
                className="inline-flex items-center gap-2 mt-12 text-[#B8956A] font-light hover:text-[#A17E54] transition-colors border-b border-[#B8956A] hover:border-[#A17E54] pb-1 cursor-pointer whitespace-nowrap"
              >
                Entenda a abordagem completa
                <i className="ri-arrow-right-line"></i>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Pilares da Estruturação */}
      <section id="pilares" className="py-36 bg-gradient-to-b from-[#FAF8F5] to-white">
        <div className="max-w-[1200px] mx-auto px-20">
          <div className="text-center mb-24">
            <div className="mb-6">
              <span className="text-[#B8956A] text-xs font-medium tracking-[0.2em] uppercase">
                ÍA Desenvolvimento Comercial • Metodologia Sistêmica
              </span>
            </div>
            <h2 className="text-[50px] font-light text-[#4A3F35] font-serif italic">
              Pilares da Estruturação
            </h2>
            <h2 className="text-[50px] font-extralight text-[#B8956A] font-serif">
              Completa
            </h2>
          </div>

          <div className="grid grid-cols-3 gap-8 mb-10">
            {/* Pilar 1 */}
            <div className="bg-white rounded-[25px] p-12 hover:shadow-2xl border border-[#E8DCC8] hover:border-[#B8956A] transition-all duration-300 text-center cursor-pointer">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#B8956A]/20 to-[#E8DCC8]/30 flex items-center justify-center mx-auto mb-7">
                <span className="text-[#B8956A] text-2xl font-light font-serif">01</span>
              </div>
              <h3 className="text-xl font-medium text-[#4A3F35] mb-4">
                Inteligência de Cliente (ICP)
              </h3>
              <p className="text-[15px] leading-[1.8] text-[#6B5D52] font-light">
                Definição técnica de quem gera maior lucratividade e valor estratégico.
              </p>
            </div>

            {/* Pilar 2 */}
            <div className="bg-white rounded-[25px] p-12 hover:shadow-2xl border border-[#E8DCC8] hover:border-[#B8956A] transition-all duration-300 text-center cursor-pointer">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#B8956A]/20 to-[#E8DCC8]/30 flex items-center justify-center mx-auto mb-7">
                <span className="text-[#B8956A] text-2xl font-light font-serif">02</span>
              </div>
              <h3 className="text-xl font-medium text-[#4A3F35] mb-4">
                Arquitetura de Equipe
              </h3>
              <p className="text-[15px] leading-[1.8] text-[#6B5D52] font-light">
                Colocar as pessoas certas nas funções certas via análise de perfil.
              </p>
            </div>

            {/* Pilar 3 */}
            <div className="bg-white rounded-[25px] p-12 hover:shadow-2xl border border-[#E8DCC8] hover:border-[#B8956A] transition-all duration-300 text-center cursor-pointer">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#B8956A]/20 to-[#E8DCC8]/30 flex items-center justify-center mx-auto mb-7">
                <span className="text-[#B8956A] text-2xl font-light font-serif">03</span>
              </div>
              <h3 className="text-xl font-medium text-[#4A3F35] mb-4">
                Engenharia de Processos
              </h3>
              <p className="text-[15px] leading-[1.8] text-[#6B5D52] font-light">
                Criação do Playbook Comercial e fluxos de trabalho estruturados.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-8 max-w-[800px] mx-auto">
            {/* Pilar 4 */}
            <div className="bg-white rounded-[25px] p-12 hover:shadow-2xl border border-[#E8DCC8] hover:border-[#B8956A] transition-all duration-300 text-center cursor-pointer">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#B8956A]/20 to-[#E8DCC8]/30 flex items-center justify-center mx-auto mb-7">
                <span className="text-[#B8956A] text-2xl font-light font-serif">04</span>
              </div>
              <h3 className="text-xl font-medium text-[#4A3F35] mb-4">
                Gestão e Indicadores
              </h3>
              <p className="text-[15px] leading-[1.8] text-[#6B5D52] font-light">
                Dashboards de performance e rituais de liderança eficazes.
              </p>
            </div>

            {/* Pilar 5 */}
            <div className="bg-white rounded-[25px] p-12 hover:shadow-2xl border border-[#E8DCC8] hover:border-[#B8956A] transition-all duration-300 text-center cursor-pointer">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#B8956A]/20 to-[#E8DCC8]/30 flex items-center justify-center mx-auto mb-7">
                <span className="text-[#B8956A] text-2xl font-light font-serif">05</span>
              </div>
              <h3 className="text-xl font-medium text-[#4A3F35] mb-4">
                Integração Sistêmica
              </h3>
              <p className="text-[15px] leading-[1.8] text-[#6B5D52] font-light">
                Alinhamento total entre Marketing, Vendas e Entrega.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Metodologia - Timeline */}
      <section id="metodologia" className="py-32 bg-gradient-to-br from-[#B8956A] via-[#A17E54] to-[#8B7355]">
        <div className="max-w-[1200px] mx-auto px-20">
          <div className="text-center mb-10">
            <span className="text-[#FAF8F5] text-xs font-light tracking-[0.2em] uppercase mb-4 block">
              ÍA Desenvolvimento Comercial • Gestão de Alta Performance
            </span>
            <h2 className="text-[48px] font-light text-white font-serif italic">
              O Caminho da Transformação
            </h2>
          </div>

          <div className="relative mt-20">
            {/* Timeline Line */}
            <div className="absolute top-[60px] left-[10%] right-[10%] h-[1px] bg-white/40"></div>

            <div className="grid grid-cols-4 gap-8">
              {/* Fase 1 */}
              <div className="text-center">
                <div className="w-[120px] h-[120px] rounded-full bg-white flex items-center justify-center mx-auto mb-8 relative z-10 shadow-2xl">
                  <span className="text-[#B8956A] text-4xl font-light font-serif">1</span>
                </div>
                <h3 className="text-white text-lg font-medium uppercase mb-3 tracking-wide">
                  Diagnóstico
                </h3>
                <p className="text-[#FAF8F5] text-sm leading-[1.7] max-w-[220px] mx-auto font-light">
                  Análise situacional profunda da estrutura comercial atual
                </p>
              </div>

              {/* Fase 2 */}
              <div className="text-center">
                <div className="w-[120px] h-[120px] rounded-full bg-white flex items-center justify-center mx-auto mb-8 relative z-10 shadow-2xl">
                  <span className="text-[#B8956A] text-4xl font-light font-serif">2</span>
                </div>
                <h3 className="text-white text-lg font-medium uppercase mb-3 tracking-wide">
                  Desenho
                </h3>
                <p className="text-[#FAF8F5] text-sm leading-[1.7] max-w-[220px] mx-auto font-light">
                  Criação do modelo estratégico customizado para o negócio
                </p>
              </div>

              {/* Fase 3 */}
              <div className="text-center">
                <div className="w-[120px] h-[120px] rounded-full bg-white flex items-center justify-center mx-auto mb-8 relative z-10 shadow-2xl">
                  <span className="text-[#B8956A] text-4xl font-light font-serif">3</span>
                </div>
                <h3 className="text-white text-lg font-medium uppercase mb-3 tracking-wide">
                  Implementação
                </h3>
                <p className="text-[#FAF8F5] text-sm leading-[1.7] max-w-[220px] mx-auto font-light">
                  Execução e direcionamento estratégico da liderança
                </p>
              </div>

              {/* Fase 4 */}
              <div className="text-center">
                <div className="w-[120px] h-[120px] rounded-full bg-white flex items-center justify-center mx-auto mb-8 relative z-10 shadow-2xl">
                  <span className="text-[#B8956A] text-4xl font-light font-serif">4</span>
                </div>
                <h3 className="text-white text-lg font-medium uppercase mb-3 tracking-wide">
                  Acompanhamento
                </h3>
                <p className="text-[#FAF8F5] text-sm leading-[1.7] max-w-[220px] mx-auto font-light">
                  Monitoramento e ajustes contínuos de performance
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Qualificação */}
      <section id="qualificacao" className="grid grid-cols-2">
        {/* SIM - Coluna Esquerda */}
        <div className="bg-gradient-to-br from-[#FAF8F5] to-[#F5F1EB] px-16 py-24">
          <div className="max-w-[500px] mx-auto">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#8B9D7F] to-[#A5B89A] flex items-center justify-center mb-6 shadow-lg">
              <i className="ri-check-line text-white text-2xl"></i>
            </div>
            <h3 className="text-[#B8956A] text-xs font-medium uppercase tracking-[0.2em] mb-2">
              ÍA Desenvolvimento Comercial
            </h3>
            <h3 className="text-[#4A3F35] text-sm font-medium uppercase tracking-wider mb-6">
              Para Quem É
            </h3>
            <ul className="space-y-5">
              <li className="flex items-start gap-4 text-[#4A3F35] text-base leading-[2.0] font-light">
                <span className="text-[#B8956A] mt-2">◆</span>
                <span>Empresas com validação de mercado que buscam previsibilidade</span>
              </li>
              <li className="flex items-start gap-4 text-[#4A3F35] text-base leading-[2.0] font-light">
                <span className="text-[#B8956A] mt-2">◆</span>
                <span>Organizações que desejam profissionalização da estrutura comercial</span>
              </li>
              <li className="flex items-start gap-4 text-[#4A3F35] text-base leading-[2.0] font-light">
                <span className="text-[#B8956A] mt-2">◆</span>
                <span>Sócios que precisam sair do operacional e atuar estrategicamente</span>
              </li>
              <li className="flex items-start gap-4 text-[#4A3F35] text-base leading-[2.0] font-light">
                <span className="text-[#B8956A] mt-2">◆</span>
                <span>Líderes que buscam autonomia operacional e escalabilidade</span>
              </li>
              <li className="flex items-start gap-4 text-[#4A3F35] text-base leading-[2.0] font-light">
                <span className="text-[#B8956A] mt-2">◆</span>
                <span>Negócios prontos para investir em arquitetura de processos</span>
              </li>
            </ul>
          </div>
        </div>

        {/* NÃO - Coluna Direita */}
        <div className="bg-gradient-to-br from-[#E8DCC8] to-[#D4C8B5] px-16 py-24 relative">
          <div className="absolute left-0 top-0 bottom-0 w-[1px] bg-[#B8956A]/20"></div>
          <div className="max-w-[500px] mx-auto">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#A17E54] to-[#8B7355] flex items-center justify-center mb-6 shadow-lg">
              <i className="ri-close-line text-white text-2xl"></i>
            </div>
            <h3 className="text-[#B8956A] text-xs font-medium uppercase tracking-[0.2em] mb-2">
              ÍA Desenvolvimento Comercial
            </h3>
            <h3 className="text-[#4A3F35] text-sm font-medium uppercase tracking-wider mb-6">
              Não É Para Quem
            </h3>
            <ul className="space-y-5">
              <li className="flex items-start gap-4 text-[#6B5D52] text-base leading-[2.0] font-light">
                <span className="text-[#8B7355] mt-2">◆</span>
                <span>Busca fórmulas mágicas ou resultados instantâneos sem esforço</span>
              </li>
              <li className="flex items-start gap-4 text-[#6B5D52] text-base leading-[2.0] font-light">
                <span className="text-[#8B7355] mt-2">◆</span>
                <span>Quer apenas scripts prontos sem estratégia personalizada</span>
              </li>
              <li className="flex items-start gap-4 text-[#6B5D52] text-base leading-[2.0] font-light">
                <span className="text-[#8B7355] mt-2">◆</span>
                <span>Procura ferramentas isoladas sem visão sistêmica</span>
              </li>
              <li className="flex items-start gap-4 text-[#6B5D52] text-base leading-[2.0] font-light">
                <span className="text-[#8B7355] mt-2">◆</span>
                <span>Não está disposto a investir em transformação estrutural</span>
              </li>
              <li className="flex items-start gap-4 text-[#6B5D52] text-base leading-[2.0] font-light">
                <span className="text-[#8B7355] mt-2">◆</span>
                <span>Prefere treinamentos motivacionais a engenharia de processos</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Resultados Observados */}
      <section className="py-32 bg-white">
        <div className="max-w-[1200px] mx-auto px-20">
          <div className="text-center mb-16">
            <div className="mb-4">
              <span className="text-[#B8956A] text-xs font-medium tracking-[0.2em] uppercase">
                ÍA Desenvolvimento Comercial • Ativos Estratégicos
              </span>
            </div>
            <h2 className="text-[44px] font-light text-[#4A3F35] font-serif italic">
              Resultados Observados
            </h2>
          </div>

          <div className="grid grid-cols-3 gap-6">
            {/* Card 1 */}
            <div className="bg-gradient-to-br from-white to-[#FAF8F5] rounded-[25px] p-10 shadow-lg hover:shadow-2xl border border-[#E8DCC8] hover:border-[#B8956A] transition-all duration-300 cursor-pointer">
              <div className="mb-5">
                <i className="ri-line-chart-line text-[#B8956A] text-3xl"></i>
              </div>
              <h3 className="text-[28px] font-light text-[#4A3F35] mb-3 font-serif">
                Clareza de Faturamento
              </h3>
              <p className="text-sm text-[#6B5D52] leading-relaxed font-light">
                Previsibilidade e controle sobre receitas futuras
              </p>
            </div>

            {/* Card 2 */}
            <div className="bg-gradient-to-br from-white to-[#FAF8F5] rounded-[25px] p-10 shadow-lg hover:shadow-2xl border border-[#E8DCC8] hover:border-[#B8956A] transition-all duration-300 cursor-pointer">
              <div className="mb-5">
                <i className="ri-team-line text-[#B8956A] text-3xl"></i>
              </div>
              <h3 className="text-[28px] font-light text-[#4A3F35] mb-3 font-serif">
                Autonomia Operacional
              </h3>
              <p className="text-sm text-[#6B5D52] leading-relaxed font-light">
                Equipes que executam sem dependência constante
              </p>
            </div>

            {/* Card 3 */}
            <div className="bg-gradient-to-br from-white to-[#FAF8F5] rounded-[25px] p-10 shadow-lg hover:shadow-2xl border border-[#E8DCC8] hover:border-[#B8956A] transition-all duration-300 cursor-pointer">
              <div className="mb-5">
                <i className="ri-user-heart-line text-[#B8956A] text-3xl"></i>
              </div>
              <h3 className="text-[28px] font-light text-[#4A3F35] mb-3 font-serif">
                Redução de Turnover
              </h3>
              <p className="text-sm text-[#6B5D52] leading-relaxed font-light">
                Pessoas certas nos lugares certos, maior retenção
              </p>
            </div>

            {/* Card 4 */}
            <div className="bg-gradient-to-br from-white to-[#FAF8F5] rounded-[25px] p-10 shadow-lg hover:shadow-2xl border border-[#E8DCC8] hover:border-[#B8956A] transition-all duration-300 cursor-pointer">
              <div className="mb-5">
                <i className="ri-refresh-line text-[#B8956A] text-3xl"></i>
              </div>
              <h3 className="text-[28px] font-light text-[#4A3F35] mb-3 font-serif">
                Aumento do LTV
              </h3>
              <p className="text-sm text-[#6B5D52] leading-relaxed font-light">
                Clientes mais valiosos e relacionamentos duradouros
              </p>
            </div>

            {/* Card 5 */}
            <div className="bg-gradient-to-br from-white to-[#FAF8F5] rounded-[25px] p-10 shadow-lg hover:shadow-2xl border border-[#E8DCC8] hover:border-[#B8956A] transition-all duration-300 cursor-pointer">
              <div className="mb-5">
                <i className="ri-building-line text-[#B8956A] text-3xl"></i>
              </div>
              <h3 className="text-[28px] font-light text-[#4A3F35] mb-3 font-serif">
                Valorização do Equity
              </h3>
              <p className="text-sm text-[#6B5D52] leading-relaxed font-light">
                Empresa mais valiosa e preparada para crescimento
              </p>
            </div>

            {/* Card 6 */}
            <div className="bg-gradient-to-br from-white to-[#FAF8F5] rounded-[25px] p-10 shadow-lg hover:shadow-2xl border border-[#E8DCC8] hover:border-[#B8956A] transition-all duration-300 cursor-pointer">
              <div className="mb-5">
                <i className="ri-shield-check-line text-[#B8956A] text-3xl"></i>
              </div>
              <h3 className="text-[28px] font-light text-[#4A3F35] mb-3 font-serif">
                Processos Sólidos
              </h3>
              <p className="text-sm text-[#6B5D52] leading-relaxed font-light">
                Operação que funciona independente de pessoas
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section id="contato" className="bg-gradient-to-br from-[#B8956A] via-[#A17E54] to-[#8B7355] py-32">
        <div className="max-w-[900px] mx-auto px-20 text-center">
          <div className="mb-6">
            <span className="text-[#FAF8F5] text-xs font-light tracking-[0.2em] uppercase">
              ÍA Desenvolvimento Comercial • Gestão de Alta Performance
            </span>
          </div>

          <h2 className="text-[52px] font-light text-white mb-10 font-serif leading-tight italic">
            Pronto para profissionalizar<br />sua estrutura comercial?
          </h2>
          
          <p className="text-[18px] text-white/90 leading-[1.8] mb-14 max-w-[80%] mx-auto font-light">
            Vamos entender se o seu momento de negócio está alinhado com a minha metodologia de atuação estratégica.
          </p>

          <a 
            href="https://wa.me/5541998065890"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 bg-white text-[#B8956A] px-14 py-6 rounded-full text-[17px] font-medium hover:bg-[#FAF8F5] hover:shadow-2xl transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-whatsapp-line text-xl"></i>
            Falar com a Estrategista Agora
          </a>

          <div className="mt-8 flex items-center justify-center gap-2 text-white text-[15px] font-light">
            <i className="ri-phone-line"></i>
            <span>+55 41 998065890</span>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-b from-[#FAF8F5] to-white border-t border-[#E8DCC8] py-16">
        <div className="max-w-[1400px] mx-auto px-20">
          <div className="grid grid-cols-3 gap-16 mb-12">
            {/* Coluna Esquerda */}
            <div className="col-span-1">
              <div className="flex flex-col mb-6">
                <span className="text-[#B8956A] text-4xl font-bold tracking-tight font-serif italic">ÍA</span>
                <span className="text-[#8B7355] text-[10px] font-light tracking-[0.2em] uppercase -mt-1">Desenvolvimento Comercial</span>
              </div>
              <p className="text-sm text-[#6B5D52] leading-[1.9] max-w-[85%] mb-8 font-light">
                Estruturação comercial sistêmica e gestão de alta performance para empresas que buscam crescimento previsível e escalável.
              </p>
            </div>

            {/* Coluna Central */}
            <div className="col-span-1">
              <h4 className="text-[#B8956A] text-xs font-medium uppercase tracking-[0.15em] mb-5">
                Contato
              </h4>
              <div className="space-y-5">
                <div>
                  <p className="text-[#8B7355] text-xs mb-2 font-light">Telefone</p>
                  <a 
                    href="https://wa.me/5541998065890"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#4A3F35] hover:text-[#B8956A] transition-colors cursor-pointer whitespace-nowrap font-light"
                  >
                    +55 41 998065890
                  </a>
                </div>
              </div>
            </div>

            {/* Coluna Direita */}
            <div className="col-span-1 flex justify-end">
              <div className="w-[200px] h-[280px] rounded-[25px] overflow-hidden border-2 border-[#B8956A]/30 shadow-xl">
                <img 
                  src="https://static.readdy.ai/image/b1a50788a9da1edd0d71f16ab7984f8b/d3671c79e8eb7738057c5a960b3c2905.jpeg"
                  alt="ÍA Desenvolvimento Comercial Professional"
                  className="w-full h-full object-cover object-center"
                />
              </div>
            </div>
          </div>

          {/* Copyright */}
          <div className="border-t border-[#E8DCC8] pt-8 flex items-center justify-between">
            <p className="text-[#8B7355] text-[13px] font-light">
              © 2025 ÍA Desenvolvimento Comercial - Todos os direitos reservados
            </p>
            <div className="flex items-center gap-6">
              <a 
                href="https://readdy.ai/?ref=logo"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#B8956A] text-[13px] hover:text-[#A17E54] transition-colors cursor-pointer whitespace-nowrap font-light"
              >
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* WhatsApp Floating Button */}
      <a
        href="https://wa.me/5541998065890"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 w-16 h-16 bg-[#B8956A] rounded-full flex items-center justify-center shadow-2xl hover:w-[70px] hover:h-[70px] hover:bg-[#A17E54] transition-all duration-300 z-50 cursor-pointer"
        style={{
          boxShadow: '0 8px 24px rgba(184, 149, 106, 0.4)'
        }}
      >
        <i className="ri-whatsapp-fill text-white text-3xl"></i>
      </a>
    </div>
  );
}
